import * as React from "react";
import { StatusPill } from "./PageHeader";

// KeyValue — render a server object as labelled rows.
//
// Extracted from operations.tsx's StatusPanel, which had grown this logic inline for one caller.
// The second occurrence is the signal (CLAUDE.md rule 4), and there are now many: every cluster
// facet (/cluster/ca, /cluster/identity, /cluster/garbd, /cluster/mesh) is a flat object whose
// fields are the answer, and so is nearly every module's /status.
//
// The formatting decisions are the point, and each one exists because the naive version misleads:
//
//   * booleans become a PILL, not "true"/"false". A dense table of the word "false" reads as
//     noise; a red pill reads as a state.
//   * an empty string, null and undefined are all rendered as "not set" in muted text rather
//     than as blank space, because blank space reads as "nothing here to worry about" and the
//     absence of a value is frequently the fault.
//   * timestamps and durations are left to the caller. This component does not guess at meaning
//     from a field's name; a `format` per row is explicit and never wrong.
export interface KVRow {
  key: string;
  label?: string;
  value: unknown;
  /** Full control when the raw value is not the thing to show. */
  render?: (value: unknown) => React.ReactNode;
  /** Shown under the row: what this field actually means, in the operator's terms. */
  hint?: React.ReactNode;
  /** For a boolean, whether `true` is the GOOD state. Default true. Inverted for fields that
   *  name a problem (`degraded`, `held`, `evicted`) — otherwise a red pill would mark health. */
  goodWhen?: boolean;
}

function humanise(k: string): string {
  return k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

export function KeyValue({ rows, dense = false }: { rows: KVRow[]; dense?: boolean }) {
  return (
    <div className="divide-y divide-line">
      {rows.map((r) => {
        const label = r.label ?? humanise(r.key);
        let node: React.ReactNode;
        if (r.render) {
          node = r.render(r.value);
        } else if (typeof r.value === "boolean") {
          const good = r.goodWhen === false ? !r.value : r.value;
          node = <StatusPill tone={good ? "ok" : "bad"}>{r.value ? "yes" : "no"}</StatusPill>;
        } else if (r.value === null || r.value === undefined || r.value === "") {
          // Never blank. Blank reads as "fine"; this is an absence, and absences are often the fault.
          node = <span className="text-muted">not set</span>;
        } else if (Array.isArray(r.value)) {
          node = r.value.length ? (
            <span className="flex flex-wrap justify-end gap-1">
              {r.value.map((v, i) => (
                <span key={i} className="rounded border border-line bg-bg2 px-1.5 py-0.5 font-mono text-[11px]">
                  {String(v)}
                </span>
              ))}
            </span>
          ) : (
            <span className="text-muted">none</span>
          );
        } else {
          node = <span className="font-mono tabular">{String(r.value)}</span>;
        }
        return (
          <div key={r.key} className={dense ? "px-4 py-1.5" : "px-4 py-2.5"}>
            {/* Wraps instead of squeezing: a long label and a long value each get the line they need
                on a phone, and words break at word boundaries (break-all split them mid-letter). */}
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
              <span className="min-w-0 text-[12.5px] text-muted">{label}</span>
              <span className="ml-auto min-w-0 max-w-full text-right text-[13.5px] [overflow-wrap:anywhere]">{node}</span>
            </div>
            {r.hint && <div className="mt-1 text-[12.5px] leading-relaxed text-muted">{r.hint}</div>}
          </div>
        );
      })}
    </div>
  );
}
